import { CircularProgress, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useApi } from '@backstage/core-plugin-api';
import { useAsync } from 'react-use';
import { useState } from 'react';
import { adServiceApiRef } from '../../services/ad.service';

type AADGroupNameDropdownProps = {
  value: string;
  error: boolean;
  helperText: string;
  onChange: (event: unknown) => void;
  emit?: 'id' | 'name';
};

export const AADGroupNameDropdown = (props: AADGroupNameDropdownProps) => {
  const { onChange, value, error, helperText, emit = 'id', ...other } = props;
  const adService = useApi(adServiceApiRef);

  const [aadGroups, setAadGroups] = useState<{ groupId: string; groupName: string }[]>([]);

  const { loading } = useAsync(async () => {
    try {
      const membership = await adService.getUsersGroupMembershipAzureAD();
      const groups = (membership?.outputGroups ?? [])
        .filter(g => !!g.groupId && !!g.groupName)
        .map(g => ({ groupId: g.groupId, groupName: g.groupName }))
        .sort((a, b) => a.groupName.localeCompare(b.groupName));
      setAadGroups(groups);
    } catch (err) {
      throw new Error(
        'Cannot get AAD Group Names. Please try again and if the problem persists, reach out to #Runway on Slack',
      );
    }
  });

  return (
    <>
      {loading && <CircularProgress style={{ marginLeft: 10 }} aria-label="circular-spinner" />}
      <Autocomplete
        freeSolo={false}
        autoComplete
        includeInputInList
        filterSelectedOptions
        loading={!aadGroups}
        loadingText="Loading AD Groups"
        value={emit === 'id' ? (aadGroups.find(g => g.groupId === value)?.groupName ?? null) : value || null}
        onChange={(_, newValue) => {
          const selectedName = String(newValue ?? '');
          const found = aadGroups.find(g => g.groupName === selectedName);
          onChange(emit === 'id' ? (found?.groupId ?? '') : selectedName);
        }}
        options={aadGroups.map(g => g.groupName)}
        renderInput={params => (
          <TextField
            {...params}
            {...props}
            variant="outlined"
            label="Azure AD Group Name"
            aria-label="aadgroup-input"
            InputProps={{ ...params.InputProps }}
            error={error}
            helperText={helperText}
            required
          />
        )}
        {...other}
      />
    </>
  );
};
